import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import '../styles/components/bracket-view.scss';
import { baseURL, powerStatsAPI, CURRENT_SEASON } from '../Constants';
import { SEASON_1_BRACKET_DATA, getSeason1MatchByPosition } from '../data/season1BracketData';

// Define types for our data structure
interface Team {
  teamId: string;
  teamName: string;
  teamLogo: string | null;
  ownerId: string | null;
  season: number;
  division: string;
  twitter: string | null;
  coachID: string | null;
  website: string | null;
  seed: string;
  playoffSeed: string | null;
}

interface UpcomingMatch {
  challonge_id: string;
  team1: Team;
  team2: Team;
  isPlayoff: boolean;
  playoffPlayed: boolean;
  division: string;
  match_time: string;
  stream_url: string;
  veto: string | null;
  roundNumber: number | null;
  season: number;
  hidden: boolean;
}

interface MatchStructure {
  id: number;
  name: string;
  nextMatchId: number | null;
  nextLoserMatchId: number | null;
  tournamentRoundText: string;
  position: { top: number; left: number };
  section: 'UPPER' | 'LOWER';
  apiRoundNumber?: number;
}

// Season 1 specific interfaces
interface Season1Team {
  teamId: string;
  teamName: string;
  teamLogo: string;
  ownerId: string | null;
  season: number;
  division: string;
  twitter: string | null;
  coachID: string | null;
  website: string | null;
  seed: string;
  playoffSeed: string;
}

interface Season1Match {
  teamA: Season1Team;
  teamB: Season1Team;
  teamAScore: number | null;
  teamBScore: number | null;
}

interface Season1Round {
  roundNumber: number;
  matches: Season1Match[];
}

interface Season1BracketData {
  upperBracket: Season1Round[];
  lowerBracket: Season1Round[];
  finalMatch: Season1Match;
}

interface BracketViewProps {
  season?: number;
}

const TROPHY_BY_SEASON: Record<number, string> = {
  1: "/images/POWERSTATS.DK_1.png",
  2: "/images/POWERSTATS.DK_7.png",
  3: "/images/NCSWINTER25.png",
};

// Pure utility functions (extracted to module scope)
const parseTeamNames = (matchName: string): { team1: string, team2: string } => {
  const parts = matchName.split(' vs ');
  return {
    team1: parts[0] || 'Unknown',
    team2: parts[1] || 'Unknown'
  };
};

const formatMatchDateTime = (dateString: string): string => {
  const date = new Date(dateString);
  const month = date.getMonth() + 1;
  const day = date.getDate();
  const hours = date.getHours().toString().padStart(2, '0');
  const minutes = date.getMinutes().toString().padStart(2, '0');
  return `${day}/${month} ${hours}:${minutes}`;
};

const truncateTeamName = (name: string, maxLength: number = 12): string => {
  if (name.length <= maxLength) return name;
  return name.substring(0, maxLength - 2) + '..';
};

// Simple presentational components (no closure deps)
const ConnectorLine = ({ start, end, color = '#4294d0' }: { start: { x: number; y: number }, end: { x: number; y: number }, color?: string }) => {
  const controlPoint = {
    x: start.x + (end.x - start.x) / 2,
    y: start.y
  };
  const path = `M ${start.x} ${start.y} C ${controlPoint.x} ${controlPoint.y}, ${controlPoint.x} ${end.y}, ${end.x} ${end.y}`;
  return (
    <path d={path} stroke={color} strokeWidth={2} fill="none" />
  );
};

const RoundHeader = ({ title, position }: { title: string, position: { top: number; left: number } }) => {
  return (
    <div className="round-header" style={{position: 'absolute', ...position}}>
      {title}
    </div>
  );
};

// Shared context interface for bracket match components
interface BracketCtx {
  matches: UpcomingMatch[];
  playedMatches: any[];
  season: number;
  teamPlaceholderImage: string;
  isMatchPlayed: (challongeId: string) => boolean;
  getTeamLogoUrl: (logoUrl: string | null, teamName?: string) => string;
  handleSeason2MatchClick: (matchStructure: MatchStructure) => void;
  handleSeason1MatchClick: (matchId: number) => void;
  getMatchTeamsForSeason1: (matchId: number) => { teamA: Season1Team; teamB: Season1Team; scores: { teamAScore: number | null; teamBScore: number | null } } | null;
}

// Season 2 match box component
const Season2MatchBox = ({ matchStructure, ctx }: { matchStructure: MatchStructure; ctx: BracketCtx }) => {
  const { id, position, tournamentRoundText, apiRoundNumber, name } = matchStructure;
  const { matches, playedMatches, teamPlaceholderImage, isMatchPlayed: checkPlayed, handleSeason2MatchClick } = ctx;

  const roundToFind = apiRoundNumber !== undefined ? apiRoundNumber : id;
  const matchData = matches.find(m => m.roundNumber === roundToFind);
  const team1 = matchData?.team1;
  const team2 = matchData?.team2;
  const parsedTeams = parseTeamNames(name);
  const isClickable = !!matchData?.challonge_id;
  const hasPlayed = matchData ? checkPlayed(matchData.challonge_id) : false;

  let team1Won = false;
  let team2Won = false;
  let team1Score: number | null = null;
  let team2Score: number | null = null;

  if (hasPlayed && matchData) {
    const playedMatch = playedMatches.find(pm => pm.matchid === matchData.challonge_id);
    if (playedMatch) {
      const teamsInSameOrder =
        playedMatch.team1?.teamId === matchData.team1?.teamId ||
        playedMatch.team1?.teamName === matchData.team1?.teamName;

      if (teamsInSameOrder) {
        team1Won = playedMatch.team1won === true;
        team2Won = playedMatch.team1won === false;
        team1Score = playedMatch.team1score ?? null;
        team2Score = playedMatch.team2score ?? null;
      } else {
        team1Won = playedMatch.team1won === false;
        team2Won = playedMatch.team1won === true;
        team1Score = playedMatch.team2score ?? null;
        team2Score = playedMatch.team1score ?? null;
      }
    }
  }

  const formatScoreDisplay = (score: number | null): string => {
    if (score === null) return '';
    return ` (${score})`;
  };

  return (
    <div
      className={`match-box ${isClickable ? 'clickable' : ''}`}
      style={{
        position: 'absolute',
        ...position,
        cursor: isClickable ? 'pointer' : 'default'
      }}
      onClick={() => isClickable && handleSeason2MatchClick(matchStructure)}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => { if ((e.key === 'Enter' || e.key === ' ') && isClickable) handleSeason2MatchClick(matchStructure); }}
    >
      <div className="match-header">
        <span className="match-number">#{id}</span>
        {matchData && !hasPlayed && (
          <span className="match-time">{formatMatchDateTime(matchData.match_time)}</span>
        )}
        <span className="match-type">{tournamentRoundText}</span>
      </div>
      <div className="match-teams">
        <div className={`team ${team1Won ? 'winner' : ''} ${hasPlayed && !team1Won ? 'loser' : ''}`}>
          <img
            src={`${baseURL}/Team/${team1?.teamId}/logo` || teamPlaceholderImage}
            alt={`${team1?.teamName || parsedTeams.team1} logo`}
            className="team-logo"
            onError={(e) => {
              (e.target as HTMLImageElement).src = teamPlaceholderImage;
            }}
          />
          <span className="team-name">
            {truncateTeamName(team1?.teamName || parsedTeams.team1)}
            {hasPlayed && team1Score !== null && (
              <span className="team-score">{formatScoreDisplay(team1Score)}</span>
            )}
          </span>
        </div>
        <div className={`team ${team2Won ? 'winner' : ''} ${hasPlayed && !team2Won ? 'loser' : ''}`}>
          <img
            src={`${baseURL}/Team/${team2?.teamId}/logo` || teamPlaceholderImage}
            alt={`${team2?.teamName || parsedTeams.team2} logo`}
            className="team-logo"
            onError={(e) => {
              (e.target as HTMLImageElement).src = teamPlaceholderImage;
            }}
          />
          <span className="team-name">
            {truncateTeamName(team2?.teamName || parsedTeams.team2)}
            {hasPlayed && team2Score !== null && (
              <span className="team-score">{formatScoreDisplay(team2Score)}</span>
            )}
          </span>
        </div>
      </div>
    </div>
  );
};

// Season 1 match box component
const Season1MatchBox = ({ matchStructure, ctx }: { matchStructure: MatchStructure; ctx: BracketCtx }) => {
  const { id, position, tournamentRoundText, name } = matchStructure;
  const { teamPlaceholderImage, handleSeason1MatchClick, getTeamLogoUrl, getMatchTeamsForSeason1 } = ctx;

  const matchTeams = getMatchTeamsForSeason1(id);

  if (!matchTeams) {
    const parsedTeams = parseTeamNames(name);
    return (
      <div
        className="match-box"
        style={{position: 'absolute', ...position}}
        onClick={() => handleSeason1MatchClick(id)}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') handleSeason1MatchClick(id); }}
      >
        <div className="match-header">
          <span className="match-number">#{id}</span>
          <span className="match-type">{tournamentRoundText}</span>
        </div>
        <div className="match-teams">
          <div className="team">
            <img src={teamPlaceholderImage} alt={`${parsedTeams.team1} logo`} className="team-logo" />
            <span className="team-name">{truncateTeamName(parsedTeams.team1)}</span>
          </div>
          <div className="team">
            <img src={teamPlaceholderImage} alt={`${parsedTeams.team2} logo`} className="team-logo" />
            <span className="team-name">{truncateTeamName(parsedTeams.team2)}</span>
          </div>
        </div>
      </div>
    );
  }

  const { teamA, teamB, scores } = matchTeams;
  const hasPlayed = scores.teamAScore !== null && scores.teamBScore !== null;
  const teamAWon = hasPlayed && scores.teamAScore! > scores.teamBScore!;
  const teamBWon = hasPlayed && scores.teamBScore! > scores.teamAScore!;

  return (
    <div
      className="match-box"
      style={{position: 'absolute', ...position}}
      onClick={() => handleSeason1MatchClick(id)}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') handleSeason1MatchClick(id); }}
    >
      <div className="match-header">
        <span className="match-number">#{id}</span>
        <span className="match-type">{tournamentRoundText}</span>
      </div>
      <div className="match-teams">
        <div className={`team ${teamAWon ? 'winner' : ''} ${hasPlayed && !teamAWon ? 'loser' : ''}`}>
          <img
            src={getTeamLogoUrl(teamA.teamLogo, teamA.teamName)}
            alt={`${teamA.teamName} logo`}
            className="team-logo"
            onError={(e) => { (e.target as HTMLImageElement).src = teamPlaceholderImage; }}
          />
          <span className="team-name">
            {truncateTeamName(teamA.teamName)}
            {hasPlayed && <span className="team-score"> ({scores.teamAScore})</span>}
          </span>
        </div>
        <div className={`team ${teamBWon ? 'winner' : ''} ${hasPlayed && !teamBWon ? 'loser' : ''}`}>
          <img
            src={getTeamLogoUrl(teamB.teamLogo, teamB.teamName)}
            alt={`${teamB.teamName} logo`}
            className="team-logo"
            onError={(e) => { (e.target as HTMLImageElement).src = teamPlaceholderImage; }}
          />
          <span className="team-name">
            {truncateTeamName(teamB.teamName)}
            {hasPlayed && <span className="team-score"> ({scores.teamBScore})</span>}
          </span>
        </div>
      </div>
    </div>
  );
};

// Mobile match card component
const MobileMatchCard = ({ matchStructure, ctx }: { matchStructure: MatchStructure; ctx: BracketCtx }) => {
  const { id, tournamentRoundText, apiRoundNumber, name } = matchStructure;
  const { season, matches, playedMatches, teamPlaceholderImage, isMatchPlayed: checkPlayed, getTeamLogoUrl, handleSeason2MatchClick, handleSeason1MatchClick, getMatchTeamsForSeason1 } = ctx;

  if (season === 1) {
    const matchTeams = getMatchTeamsForSeason1(id);
    if (!matchTeams) {
      const parsedTeams = parseTeamNames(name);
      return (
        <div className="mobile-match-card">
          <div className="card-header">
            <span className="match-number">#{id}</span>
            <span className="match-type">{tournamentRoundText}</span>
          </div>
          <div className="teams-container">
            <div className="team-row">
              <img src={teamPlaceholderImage} alt="" className="team-logo" />
              <span className="team-name">{truncateTeamName(parsedTeams.team1, 18)}</span>
            </div>
            <div className="team-row">
              <img src={teamPlaceholderImage} alt="" className="team-logo" />
              <span className="team-name">{truncateTeamName(parsedTeams.team2, 18)}</span>
            </div>
          </div>
        </div>
      );
    }

    const { teamA, teamB, scores } = matchTeams;
    const hasPlayed = scores.teamAScore !== null && scores.teamBScore !== null;
    const teamAWon = hasPlayed && scores.teamAScore! > scores.teamBScore!;
    const teamBWon = hasPlayed && scores.teamBScore! > scores.teamAScore!;

    return (
      <div className="mobile-match-card" onClick={() => handleSeason1MatchClick(id)} role="button" tabIndex={0} onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') handleSeason1MatchClick(id); }}>
        <div className="card-header">
          <span className="match-number">#{id}</span>
          <span className="match-type">{tournamentRoundText}</span>
        </div>
        <div className="teams-container">
          <div className={`team-row ${teamAWon ? 'winner' : ''} ${hasPlayed && !teamAWon ? 'loser' : ''}`}>
            <img src={getTeamLogoUrl(teamA.teamLogo, teamA.teamName)} alt={teamA.teamName} className="team-logo" onError={(e) => { (e.target as HTMLImageElement).src = teamPlaceholderImage; }} />
            <span className="team-name">{truncateTeamName(teamA.teamName, 18)}</span>
            {hasPlayed && <span className="team-score">{scores.teamAScore}</span>}
          </div>
          <div className={`team-row ${teamBWon ? 'winner' : ''} ${hasPlayed && !teamBWon ? 'loser' : ''}`}>
            <img src={getTeamLogoUrl(teamB.teamLogo, teamB.teamName)} alt={teamB.teamName} className="team-logo" onError={(e) => { (e.target as HTMLImageElement).src = teamPlaceholderImage; }} />
            <span className="team-name">{truncateTeamName(teamB.teamName, 18)}</span>
            {hasPlayed && <span className="team-score">{scores.teamBScore}</span>}
          </div>
        </div>
      </div>
    );
  }

  const roundToFind = apiRoundNumber !== undefined ? apiRoundNumber : id;
  const matchData = matches.find(m => m.roundNumber === roundToFind);
  const team1 = matchData?.team1;
  const team2 = matchData?.team2;
  const parsedTeams = parseTeamNames(name);
  const isClickable = !!matchData?.challonge_id;
  const hasPlayed = matchData ? checkPlayed(matchData.challonge_id) : false;

  let team1Won = false;
  let team2Won = false;
  let team1Score: number | null = null;
  let team2Score: number | null = null;

  if (hasPlayed && matchData) {
    const playedMatch = playedMatches.find(pm => pm.matchid === matchData.challonge_id);
    if (playedMatch) {
      const teamsInSameOrder = playedMatch.team1?.teamId === matchData.team1?.teamId ||
                               playedMatch.team1?.teamName === matchData.team1?.teamName;
      if (teamsInSameOrder) {
        team1Won = playedMatch.team1won === true;
        team2Won = playedMatch.team1won === false;
        team1Score = playedMatch.team1score ?? null;
        team2Score = playedMatch.team2score ?? null;
      } else {
        team1Won = playedMatch.team1won === false;
        team2Won = playedMatch.team1won === true;
        team1Score = playedMatch.team2score ?? null;
        team2Score = playedMatch.team1score ?? null;
      }
    }
  }

  return (
    <div
      className={`mobile-match-card ${isClickable ? 'clickable' : ''}`}
      onClick={() => isClickable && handleSeason2MatchClick(matchStructure)}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => { if ((e.key === 'Enter' || e.key === ' ') && isClickable) handleSeason2MatchClick(matchStructure); }}
    >
      <div className="card-header">
        <span className="match-number">#{id}</span>
        {matchData && !hasPlayed && (
          <span className="match-time">{formatMatchDateTime(matchData.match_time)}</span>
        )}
        <span className="match-type">{tournamentRoundText}</span>
      </div>
      <div className="teams-container">
        <div className={`team-row ${team1Won ? 'winner' : ''} ${hasPlayed && !team1Won ? 'loser' : ''}`}>
          <img src={team1 ? `${baseURL}/Team/${team1.teamId}/logo` : teamPlaceholderImage} alt={team1?.teamName || parsedTeams.team1} className="team-logo" onError={(e) => { (e.target as HTMLImageElement).src = teamPlaceholderImage; }} />
          <span className="team-name">{truncateTeamName(team1?.teamName || parsedTeams.team1, 18)}</span>
          {hasPlayed && team1Score !== null && <span className="team-score">{team1Score}</span>}
        </div>
        <div className={`team-row ${team2Won ? 'winner' : ''} ${hasPlayed && !team2Won ? 'loser' : ''}`}>
          <img src={team2 ? `${baseURL}/Team/${team2.teamId}/logo` : teamPlaceholderImage} alt={team2?.teamName || parsedTeams.team2} className="team-logo" onError={(e) => { (e.target as HTMLImageElement).src = teamPlaceholderImage; }} />
          <span className="team-name">{truncateTeamName(team2?.teamName || parsedTeams.team2, 18)}</span>
          {hasPlayed && team2Score !== null && <span className="team-score">{team2Score}</span>}
        </div>
      </div>
    </div>
  );
};

// Mobile bracket view component
const MobileBracketView = ({ matchPositions, trophyImagePath, ctx }: { matchPositions: MatchStructure[]; trophyImagePath: string; ctx: BracketCtx }) => {
  const bracketRounds = [
    { title: 'UPPER BRACKET', rounds: [
      { label: 'Round 1', matchIds: [1, 2, 3, 4] },
      { label: 'Quarter Finals', matchIds: [7, 8] },
      { label: 'Upper Final', matchIds: [12] },
    ]},
    { title: 'LOWER BRACKET', rounds: [
      { label: 'Round 1', matchIds: [5, 6] },
      { label: 'Round 2', matchIds: [9, 10] },
      { label: 'Lower Semi', matchIds: [11] },
      { label: 'Lower Final', matchIds: [13] },
    ]},
    { title: 'GRAND FINAL', rounds: [
      { label: '', matchIds: [14] },
    ]},
  ];

  return (
    <div className="mobile-bracket-view">
      {bracketRounds.map((section, sectionIdx) => (
        <div key={section.title} className={`mobile-section ${sectionIdx === 0 ? 'upper' : sectionIdx === 1 ? 'lower' : 'finals'}`}>
          <div className="section-title">{section.title}</div>
          {section.rounds.map((round) => (
            <div key={round.label || 'final'} className="round-group">
              {round.label && <div className="round-label">{round.label}</div>}
              <div className="round-matches">
                {round.matchIds.map(matchId => {
                  const matchStructure = matchPositions.find(m => m.id === matchId);
                  return matchStructure ? (
                    <MobileMatchCard key={matchId} matchStructure={matchStructure} ctx={ctx} />
                  ) : null;
                })}
              </div>
            </div>
          ))}
        </div>
      ))}
      <div className="mobile-trophy">
        <img src={trophyImagePath} alt="Championship Trophy" />
      </div>
    </div>
  );
};

const BracketView: React.FC<BracketViewProps> = ({ season = CURRENT_SEASON }) => {
  const navigate = useNavigate();
  const [matches, setMatches] = useState<UpcomingMatch[]>([]);
  const [playedMatches, setPlayedMatches] = useState<any[]>([]);
  const [season1Data, setSeason1Data] = useState<Season1BracketData | null>(null);
  const [season1TeamLogos, setSeason1TeamLogos] = useState<Record<string, string>>({});
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [windowWidth, setWindowWidth] = useState<number>(typeof window !== 'undefined' ? window.innerWidth : 1200);

  // Mobile state
  const MOBILE_BREAKPOINT = 768;
  const isMobile = windowWidth <= MOBILE_BREAKPOINT;

  const teamPlaceholderImage = "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='100' height='100' viewBox='0 0 100 100'%3E%3Crect width='100' height='100' fill='%23333'/%3E%3Ctext x='50' y='50' font-size='18' text-anchor='middle' alignment-baseline='middle' font-family='sans-serif' fill='%23fff'%3ENo Logo%3C/text%3E%3C/svg%3E";

  
const trophyImagePath = TROPHY_BY_SEASON[season] ?? TROPHY_BY_SEASON[2];

  // Update window width on resize
  useEffect(() => {
    const handleResize = () => {
      setWindowWidth(window.innerWidth);
    };
    
    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);
  
  // Convert hardcoded flat data to bracket structure
  const convertHardcodedDataToBracketStructure = (): Season1BracketData => {
    // Helper to convert our data format to expected team format
    const convertTeam = (team: { name: string; id: string; seed: number; logo?: string }): Season1Team => ({
      teamId: team.id,
      teamName: team.name,
      teamLogo: team.logo || '',
      ownerId: null,
      season: 1,
      division: '',
      twitter: null,
      coachID: null,
      website: null,
      seed: team.seed.toString(),
      playoffSeed: team.seed.toString()
    });

    // Upper Bracket Round 1 (bracket positions 1-4, match IDs 24-27-25-26)
    const upperRound1: Season1Match[] = [
      // Position 1 - Match 24
      {
        teamA: convertTeam(getSeason1MatchByPosition(1)!.team1),
        teamB: convertTeam(getSeason1MatchByPosition(1)!.team2),
        teamAScore: getSeason1MatchByPosition(1)!.score1,
        teamBScore: getSeason1MatchByPosition(1)!.score2
      },
      // Position 2 - Match 27
      {
        teamA: convertTeam(getSeason1MatchByPosition(2)!.team1),
        teamB: convertTeam(getSeason1MatchByPosition(2)!.team2),
        teamAScore: getSeason1MatchByPosition(2)!.score1,
        teamBScore: getSeason1MatchByPosition(2)!.score2
      },
      // Position 3 - Match 25
      {
        teamA: convertTeam(getSeason1MatchByPosition(3)!.team1),
        teamB: convertTeam(getSeason1MatchByPosition(3)!.team2),
        teamAScore: getSeason1MatchByPosition(3)!.score1,
        teamBScore: getSeason1MatchByPosition(3)!.score2
      },
      // Position 4 - Match 26
      {
        teamA: convertTeam(getSeason1MatchByPosition(4)!.team1),
        teamB: convertTeam(getSeason1MatchByPosition(4)!.team2),
        teamAScore: getSeason1MatchByPosition(4)!.score1,
        teamBScore: getSeason1MatchByPosition(4)!.score2
      }
    ];

    // Upper Bracket Round 2 (bracket positions 7-8, match IDs 28-29)
    const upperRound2: Season1Match[] = [
      // Position 7 - Match 28
      {
        teamA: convertTeam(getSeason1MatchByPosition(7)!.team1),
        teamB: convertTeam(getSeason1MatchByPosition(7)!.team2),
        teamAScore: getSeason1MatchByPosition(7)!.score1,
        teamBScore: getSeason1MatchByPosition(7)!.score2
      },
      // Position 8 - Match 29
      {
        teamA: convertTeam(getSeason1MatchByPosition(8)!.team1),
        teamB: convertTeam(getSeason1MatchByPosition(8)!.team2),
        teamAScore: getSeason1MatchByPosition(8)!.score1,
        teamBScore: getSeason1MatchByPosition(8)!.score2
      }
    ];

    // Upper Bracket Final (bracket position 12, match ID 41)
    const upperFinal: Season1Match[] = [
      {
        teamA: convertTeam(getSeason1MatchByPosition(12)!.team1),
        teamB: convertTeam(getSeason1MatchByPosition(12)!.team2),
        teamAScore: getSeason1MatchByPosition(12)!.score1,
        teamBScore: getSeason1MatchByPosition(12)!.score2
      }
    ];

    // Lower Bracket Round 1 (bracket positions 5-6, match IDs 30-31)
    const lowerRound1: Season1Match[] = [
      // Position 5 - Match 30
      {
        teamA: convertTeam(getSeason1MatchByPosition(5)!.team1),
        teamB: convertTeam(getSeason1MatchByPosition(5)!.team2),
        teamAScore: getSeason1MatchByPosition(5)!.score1,
        teamBScore: getSeason1MatchByPosition(5)!.score2
      },
      // Position 6 - Match 31
      {
        teamA: convertTeam(getSeason1MatchByPosition(6)!.team1),
        teamB: convertTeam(getSeason1MatchByPosition(6)!.team2),
        teamAScore: getSeason1MatchByPosition(6)!.score1,
        teamBScore: getSeason1MatchByPosition(6)!.score2
      }
    ];

    // Lower Bracket Round 2 (bracket positions 9-10, match IDs 33-34)
    const lowerRound2: Season1Match[] = [
      // Position 10 - Match 34 (yes, the array is [10, 9] in the component logic)
      {
        teamA: convertTeam(getSeason1MatchByPosition(10)!.team1),
        teamB: convertTeam(getSeason1MatchByPosition(10)!.team2),
        teamAScore: getSeason1MatchByPosition(10)!.score1,
        teamBScore: getSeason1MatchByPosition(10)!.score2
      },
      // Position 9 - Match 33
      {
        teamA: convertTeam(getSeason1MatchByPosition(9)!.team1),
        teamB: convertTeam(getSeason1MatchByPosition(9)!.team2),
        teamAScore: getSeason1MatchByPosition(9)!.score1,
        teamBScore: getSeason1MatchByPosition(9)!.score2
      }
    ];

    // Lower Bracket Semi (bracket position 11, match ID 42)
    const lowerSemi: Season1Match[] = [
      {
        teamA: convertTeam(getSeason1MatchByPosition(11)!.team1),
        teamB: convertTeam(getSeason1MatchByPosition(11)!.team2),
        teamAScore: getSeason1MatchByPosition(11)!.score1,
        teamBScore: getSeason1MatchByPosition(11)!.score2
      }
    ];

    // Lower Bracket Final (bracket position 13, match ID 13)
    const lowerFinal: Season1Match[] = [
      {
        teamA: convertTeam(getSeason1MatchByPosition(13)!.team1),
        teamB: convertTeam(getSeason1MatchByPosition(13)!.team2),
        teamAScore: getSeason1MatchByPosition(13)!.score1,
        teamBScore: getSeason1MatchByPosition(13)!.score2
      }
    ];

    // Grand Final (bracket position 14, match ID 47)
    const grandFinal: Season1Match = {
      teamA: convertTeam(getSeason1MatchByPosition(14)!.team1),
      teamB: convertTeam(getSeason1MatchByPosition(14)!.team2),
      teamAScore: getSeason1MatchByPosition(14)!.score1,
      teamBScore: getSeason1MatchByPosition(14)!.score2
    };

    return {
      upperBracket: [
        { roundNumber: 5, matches: upperRound1 },
        { roundNumber: 6, matches: upperRound2 },
        { roundNumber: 10, matches: upperFinal }
      ],
      lowerBracket: [
        { roundNumber: 9, matches: lowerRound1 },
        { roundNumber: 11, matches: lowerRound2 },
        { roundNumber: 12, matches: lowerSemi },
        { roundNumber: 13, matches: lowerFinal }
      ],
      finalMatch: grandFinal
    };
  };

  // Fetch season 1 team logos from backend (match by team name)
  useEffect(() => {
    const fetchSeason1Logos = async () => {
      if (season !== 1) return;

      try {
        const response = await fetch(`${baseURL}/Team?season=1`);
        if (response.ok) {
          const teams = await response.json();
          if (Array.isArray(teams)) {
            const logoMap: Record<string, string> = {};
            teams.forEach((team: any) => {
              // API returns 'name' and 'logo' fields
              if (team.name && team.logo) {
                // Map by team name (lowercase for case-insensitive matching)
                logoMap[team.name.toLowerCase()] = team.logo;
              }
            });
            console.log('[BracketView] Loaded season 1 team logos:', Object.keys(logoMap));
            setSeason1TeamLogos(logoMap);
          }
        }
      } catch (error) {
        console.error('[BracketView] Failed to fetch season 1 team logos:', error);
      }
    };

    fetchSeason1Logos();
  }, [season]);

  // Fetch data when season changes
  useEffect(() => {
    if (season === 1) {
      fetchSeason1BracketData();
    } else {
      fetchUpcomingMatches(season);
    }
  }, [season]);

  // Fetch Season 1 bracket data (uses hardcoded data)
  const fetchSeason1BracketData = async () => {
    setIsLoading(true);
    try {
      // Use hardcoded Season 1 data instead of broken API endpoint
      // The API returns NULL scores, so we use locally stored match results
      console.log('[BracketView] Loading hardcoded Season 1 bracket data');

      // Convert our flat hardcoded data structure to the expected Season1BracketData format
      const bracketData: Season1BracketData = convertHardcodedDataToBracketStructure();
      setSeason1Data(bracketData);
    } catch (error) {
      console.error('Error loading season 1 bracket data:', error);
    } finally {
      setIsLoading(false);
    }
  };

  // Fetch Season 2+ data
  const fetchUpcomingMatches = async (seasonNum: number) => {
    setIsLoading(true);
    try {
      // Fetch upcoming matches
      const upcomingResponse = await fetch(`${powerStatsAPI}/Match/getUpcomingMatches`);
      if (!upcomingResponse.ok) {
        throw new Error('Failed to fetch upcoming matches');
      }
      
      const matchesData: UpcomingMatch[] = await upcomingResponse.json();

      // Filter matches for the selected season, playoff matches, and exclude hidden matches
      const filteredMatches = matchesData.filter(match =>
        match.season === seasonNum && match.isPlayoff && match.roundNumber !== null && !match.hidden
      );
      
      setMatches(filteredMatches);
      
      // Fetch played matches to determine winners
      try {
        const playedResponse = await fetch(`${powerStatsAPI}/Match/getAllMatches?season=${seasonNum}`);
        if (playedResponse.ok) {
          const playedData = await playedResponse.json();
          // The API returns an object with playedMatches array
          if (playedData && playedData.playedMatches && Array.isArray(playedData.playedMatches)) {
            setPlayedMatches(playedData.playedMatches);
          } else if (Array.isArray(playedData)) {
            setPlayedMatches(playedData);
          } else {
            console.error('Played matches response is not in expected format:', playedData);
            setPlayedMatches([]);
          }
        }
      } catch (error) {
        console.error('Error fetching played matches:', error);
        setPlayedMatches([]);
      }
    } catch (error) {
      console.error('Error fetching upcoming matches:', error);
    } finally {
      setIsLoading(false);
    }
  };
  
  // Calculate bracket dimensions based on container width
  const containerPadding = 40;
  const maxContentWidth = 1120; // Target width that fits in your 1152px container with margins
  
  // Calculate the scale factor to fit the entire bracket in the available width
  const getScaleFactor = () => {
    // Base width needed for the bracket content
    const baseWidth = 1100; // This covers all elements including trophy
    
    // If window is smaller than what we need, calculate scale factor
    if (windowWidth < baseWidth + containerPadding) {
      return (windowWidth - containerPadding) / baseWidth;
    }
    
    // No scaling needed if window is large enough
    return 1;
  };
  
  const scaleFactor = getScaleFactor();
  const bracketWidth = Math.min(maxContentWidth, windowWidth - containerPadding);
  const bracketHeight = 945;
  const boxWidth = 180;
  
  // Match positions - same for both seasons
  const matchPositions: MatchStructure[] = [
    // Upper bracket first round
    { id: 1, name: "1A vs 4B", nextMatchId: 7, nextLoserMatchId: null, tournamentRoundText: "BO 1", position: { top: 20, left: 20 }, section: "UPPER", apiRoundNumber: 1 },
    { id: 2, name: "2B vs 3A", nextMatchId: 7, nextLoserMatchId: null, tournamentRoundText: "BO 1", position: { top: 160, left: 20 }, section: "UPPER", apiRoundNumber: 2 },
    { id: 3, name: "1B vs 4A", nextMatchId: 8, nextLoserMatchId: null, tournamentRoundText: "BO 1", position: { top: 300, left: 20 }, section: "UPPER", apiRoundNumber: 3 },
    { id: 4, name: "2A vs 3B", nextMatchId: 8, nextLoserMatchId: null, tournamentRoundText: "BO 1", position: { top: 440, left: 20 }, section: "UPPER", apiRoundNumber: 4 },
    
    // Upper bracket second round (Quarter Finals)
    { id: 7, name: "WINNER 1 vs WINNER 2", nextMatchId: 12, nextLoserMatchId: null, tournamentRoundText: "BO 1", position: { top: 90, left: 240 }, section: "UPPER", apiRoundNumber: 7 },
    { id: 8, name: "WINNER 3 vs WINNER 4", nextMatchId: 12, nextLoserMatchId: null, tournamentRoundText: "BO 1", position: { top: 370, left: 240 }, section: "UPPER", apiRoundNumber: 8 },
    
    // Upper Final
    { id: 12, name: "WINNER 7 vs WINNER 8", nextMatchId: 14, nextLoserMatchId: 13, tournamentRoundText: "BO 3", position: { top: 220, left: 460 }, section: "UPPER", apiRoundNumber: 12 },
    
    // Grand Final
    { id: 14, name: "WINNER 12 vs WINNER 13", nextMatchId: null, nextLoserMatchId: null, tournamentRoundText: "BO 3", position: { top: 220, left: 680 }, section: "UPPER", apiRoundNumber: 14 },
    
    // Lower bracket first round
    { id: 5, name: "LOSER 1 vs LOSER 2", nextMatchId: 10, nextLoserMatchId: null, tournamentRoundText: "BO 3", position: { top: 660, left: 20 }, section: "LOWER", apiRoundNumber: 5 },
    { id: 6, name: "LOSER 3 vs LOSER 4", nextMatchId: 9, nextLoserMatchId: null, tournamentRoundText: "BO 3", position: { top: 800, left: 20 }, section: "LOWER", apiRoundNumber: 6 },
    
    // Lower bracket second round
    { id: 10, name: "WINNER 5 vs LOSER 8", nextMatchId: 11, nextLoserMatchId: null, tournamentRoundText: "BO 3", position: { top: 660, left: 240 }, section: "LOWER", apiRoundNumber: 10 },
    { id: 9, name: "LOSER 7 vs WINNER 6", nextMatchId: 11, nextLoserMatchId: null, tournamentRoundText: "BO 3", position: { top: 800, left: 240 }, section: "LOWER", apiRoundNumber: 9 },
    
    // Lower bracket semi-final
    { id: 11, name: "WINNER 9 vs WINNER 10", nextMatchId: 13, nextLoserMatchId: null, tournamentRoundText: "BO 3", position: { top: 730, left: 460 }, section: "LOWER", apiRoundNumber: 11 },
    
    // Lower bracket final
    { id: 13, name: "LOSER 12 vs WINNER 11", nextMatchId: 14, nextLoserMatchId: null, tournamentRoundText: "BO 3", position: { top: 730, left: 680 }, section: "LOWER", apiRoundNumber: 13 },
  ];
  
  // Connector lines - same for both seasons
  const connectorLines = [
    // Upper bracket
    { start: { x: boxWidth + 20, y: 92 }, end: { x: 240, y: 140 } }, // Match 1 to 7
    { start: { x: boxWidth + 20, y: 232 }, end: { x: 240, y: 185 } }, // Match 2 to 7
    { start: { x: boxWidth + 20, y: 372 }, end: { x: 240, y: 420 } }, // Match 3 to 8
    { start: { x: boxWidth + 20, y: 512 }, end: { x: 240, y: 465 } }, // Match 4 to 8
    
    { start: { x: 240 + boxWidth, y: 162 }, end: { x: 460, y: 270 } }, // Match 7 to 12
    { start: { x: 240 + boxWidth, y: 442 }, end: { x: 460, y: 310 } }, // Match 8 to 12
    
    { start: { x: 460 + boxWidth, y: 292 }, end: { x: 680, y: 292 } }, // Match 12 to 14
    
    // Lower bracket
    { start: { x: boxWidth + 20, y: 732 }, end: { x: 240, y: 732 } }, // Match 5 to 10
    { start: { x: boxWidth + 20, y: 872 }, end: { x: 240, y: 872 } }, // Match 6 to 9
    
    { start: { x: 240 + boxWidth, y: 732 }, end: { x: 460, y: 783 } }, // Match 10 to 11
    { start: { x: 240 + boxWidth, y: 872 }, end: { x: 460, y: 823 } }, // Match 9 to 11
    
    { start: { x: 460 + boxWidth, y: 802 }, end: { x: 680, y: 802 } }, // Match 11 to 13
  ];

  // Round headers
  const roundHeaders = [
    { title: 'Upper Final', position: { top: 180, left: 500 } },
    { title: 'Grand Final', position: { top: 180, left: 720 } },
    { title: 'Lower Semi', position: { top: 690, left: 490 } },
    { title: 'Lower Final', position: { top: 690, left: 710 } },
  ];
  
  // Function to map Season 1 data to match positions
  const getMatchTeamsForSeason1 = (matchId: number): { teamA: Season1Team, teamB: Season1Team, scores: { teamAScore: number | null, teamBScore: number | null } } | null => {
    if (!season1Data) return null;
    
    // Upper bracket first round
    if (matchId === 1) {
      const upperMatches = season1Data.upperBracket[0]?.matches || [];
      return upperMatches[0] ? {
        teamA: upperMatches[0].teamA,
        teamB: upperMatches[0].teamB,
        scores: {
          teamAScore: upperMatches[0].teamAScore,
          teamBScore: upperMatches[0].teamBScore
        }
      } : null;
    }
    
    if (matchId === 2) {
      const upperMatches = season1Data.upperBracket[0]?.matches || [];
      return upperMatches[1] ? {
        teamA: upperMatches[1].teamA,
        teamB: upperMatches[1].teamB,
        scores: {
          teamAScore: upperMatches[1].teamAScore,
          teamBScore: upperMatches[1].teamBScore
        }
      } : null;
    }
    
    if (matchId === 3) {
      const upperMatches = season1Data.upperBracket[0]?.matches || [];
      return upperMatches[2] ? {
        teamA: upperMatches[2].teamA,
        teamB: upperMatches[2].teamB,
        scores: {
          teamAScore: upperMatches[2].teamAScore,
          teamBScore: upperMatches[2].teamBScore
        }
      } : null;
    }
    
    if (matchId === 4) {
      const upperMatches = season1Data.upperBracket[0]?.matches || [];
      return upperMatches[3] ? {
        teamA: upperMatches[3].teamA,
        teamB: upperMatches[3].teamB,
        scores: {
          teamAScore: upperMatches[3].teamAScore,
          teamBScore: upperMatches[3].teamBScore
        }
      } : null;
    }
    
    // Upper bracket second round
    if (matchId === 7) {
      const upperMatches = season1Data.upperBracket[1]?.matches || [];
      return upperMatches[0] ? {
        teamA: upperMatches[0].teamA,
        teamB: upperMatches[0].teamB,
        scores: {
          teamAScore: upperMatches[0].teamAScore,
          teamBScore: upperMatches[0].teamBScore
        }
      } : null;
    }
    
    if (matchId === 8) {
      const upperMatches = season1Data.upperBracket[1]?.matches || [];
      return upperMatches[1] ? {
        teamA: upperMatches[1].teamA,
        teamB: upperMatches[1].teamB,
        scores: {
          teamAScore: upperMatches[1].teamAScore,
          teamBScore: upperMatches[1].teamBScore
        }
      } : null;
    }
    
    // Upper bracket final
    if (matchId === 12) {
      const upperMatches = season1Data.upperBracket[2]?.matches || [];
      return upperMatches[0] ? {
        teamA: upperMatches[0].teamA,
        teamB: upperMatches[0].teamB,
        scores: {
          teamAScore: upperMatches[0].teamAScore,
          teamBScore: upperMatches[0].teamBScore
        }
      } : null;
    }
    
    // Lower bracket first round
    if (matchId === 5) {
      const lowerMatches = season1Data.lowerBracket[0]?.matches || [];
      return lowerMatches[0] ? {
        teamA: lowerMatches[0].teamA,
        teamB: lowerMatches[0].teamB,
        scores: {
          teamAScore: lowerMatches[0].teamAScore,
          teamBScore: lowerMatches[0].teamBScore
        }
      } : null;
    }
    
    if (matchId === 6) {
      const lowerMatches = season1Data.lowerBracket[0]?.matches || [];
      return lowerMatches[1] ? {
        teamA: lowerMatches[1].teamA,
        teamB: lowerMatches[1].teamB,
        scores: {
          teamAScore: lowerMatches[1].teamAScore,
          teamBScore: lowerMatches[1].teamBScore
        }
      } : null;
    }
    
    // Lower bracket second round
    if (matchId === 9) {
      const lowerMatches = season1Data.lowerBracket[1]?.matches || [];
      return lowerMatches[1] ? {
        teamA: lowerMatches[1].teamA,
        teamB: lowerMatches[1].teamB,
        scores: {
          teamAScore: lowerMatches[1].teamAScore,
          teamBScore: lowerMatches[1].teamBScore
        }
      } : null;
    }
    
    if (matchId === 10) {
      const lowerMatches = season1Data.lowerBracket[1]?.matches || [];
      return lowerMatches[0] ? {
        teamA: lowerMatches[0].teamA,
        teamB: lowerMatches[0].teamB,
        scores: {
          teamAScore: lowerMatches[0].teamAScore,
          teamBScore: lowerMatches[0].teamBScore
        }
      } : null;
    }
    
    // Lower bracket semi-final
    if (matchId === 11) {
      const lowerMatches = season1Data.lowerBracket[2]?.matches || [];
      return lowerMatches[0] ? {
        teamA: lowerMatches[0].teamA,
        teamB: lowerMatches[0].teamB,
        scores: {
          teamAScore: lowerMatches[0].teamAScore,
          teamBScore: lowerMatches[0].teamBScore
        }
      } : null;
    }
    
    // Lower bracket final
    if (matchId === 13) {
      const lowerMatches = season1Data.lowerBracket[3]?.matches || [];
      return lowerMatches[0] ? {
        teamA: lowerMatches[0].teamA,
        teamB: lowerMatches[0].teamB,
        scores: {
          teamAScore: lowerMatches[0].teamAScore,
          teamBScore: lowerMatches[0].teamBScore
        }
      } : null;
    }
    
    // Grand final
    if (matchId === 14) {
      return {
        teamA: season1Data.finalMatch.teamA,
        teamB: season1Data.finalMatch.teamB,
        scores: {
          teamAScore: season1Data.finalMatch.teamAScore,
          teamBScore: season1Data.finalMatch.teamBScore
        }
      };
    }
    
    return null;
  };
  
  // Check if a match is played
  const isMatchPlayed = (challongeId: string): boolean => {
    if (!Array.isArray(playedMatches)) return false;
    return playedMatches.some(played => played.matchid === challongeId);
  };

  // Team logo helper function - for Season 1, lookup by team name
  const getTeamLogoUrl = (logoUrl: string | null, teamName?: string) => {
    // For Season 1, try to find logo by team name from fetched data
    if (season === 1 && teamName) {
      const cachedLogo = season1TeamLogos[teamName.toLowerCase()];
      if (cachedLogo) {
        return cachedLogo;
      }
    }
    // Fallback to provided URL or placeholder
    if (!logoUrl) return teamPlaceholderImage;
    return logoUrl;
  };

  // Handle match box click for Season 2
  const handleSeason2MatchClick = (matchStructure: MatchStructure) => {
    const { apiRoundNumber } = matchStructure;
    
    // Find the actual match data if available
    const roundToFind = apiRoundNumber !== undefined ? apiRoundNumber : matchStructure.id;
    const matchData = matches.find(m => m.roundNumber === roundToFind);
    
    if (matchData && matchData.challonge_id) {
      // Navigate to the match page
      navigate(`/matches/${matchData.challonge_id}`);
    } else {
    }
  };

  // Handle match box click for Season 1
  const handleSeason1MatchClick = (matchId: number) => {
    // For Season 1, we could implement navigation if we store match IDs
    // This is currently a placeholder since we don't have direct match IDs for Season 1

    // If we had match IDs for Season 1, we could navigate like this:
    // const season1MatchId = getMatchIdForSeason1(matchId);
    // if (season1MatchId) {
    //   navigate(`/matches/${season1MatchId}`);
    // }
  };

  // Build shared context for extracted bracket components
  const bracketCtx: BracketCtx = {
    matches,
    playedMatches,
    season,
    teamPlaceholderImage,
    isMatchPlayed,
    getTeamLogoUrl,
    handleSeason2MatchClick,
    handleSeason1MatchClick,
    getMatchTeamsForSeason1,
  };

  return (
    <>
      {isLoading ? (
        <div className="loading">
          <div className="loading-spinner"></div>
          <p>Loading bracket...</p>
        </div>
      ) : isMobile ? (
        // MOBILE VIEW - list/card layout
        <div className="custom-bracket-container mobile">
          <MobileBracketView matchPositions={matchPositions} trophyImagePath={trophyImagePath} ctx={bracketCtx} />
        </div>
      ) : (
        // DESKTOP VIEW - existing hardcoded bracket
        <div className="custom-bracket-container" style={{ overflowX: 'hidden' }}>
          <div className="bracket-wrapper"
               style={{
                 width: bracketWidth,
                 height: bracketHeight,
                 position: 'relative',
                 transform: scaleFactor < 1 ? `scale(${scaleFactor})` : 'none',
                 transformOrigin: 'top left'
               }}>
            {/* Division titles */}
            <div className="bracket-section-title upper" style={{ top: -20, left: 0, width: '100%' }}>UPPER BRACKET</div>

            {/* The dividing line */}
            <div className="bracket-divider" style={{ top: 590, left: 0, width: '100%' }} />

            <div className="bracket-section-title lower" style={{ top: 600, left: 0, width: '100%' }}>LOWER BRACKET</div>

            {/* Round headers */}
            {roundHeaders.map((header, index) => (
              <RoundHeader key={`header-${index}`} title={header.title} position={header.position} />
            ))}

            {/* Trophy image - Compact positioning */}
            <div className="trophy-image" style={{
              position: 'absolute',
              top: 180,
              left: 780,
              width: 384,
              height: 216
            }}>
              <img src={trophyImagePath} alt="Championship Trophy" style={{ width: '100%', height: '100%' }} />
            </div>

            {/* SVG for connector lines */}
            <svg width={bracketWidth} height={bracketHeight} style={{ position: 'absolute', top: 0, left: 0, zIndex: 0 }}>
              {connectorLines.map((line, index) => (
                <ConnectorLine key={`line-${index}`} start={line.start} end={line.end} />
              ))}
            </svg>

            {/* Match boxes - render based on season */}
            {matchPositions.map((matchStructure) => (
              season === 1 ? (
                <Season1MatchBox
                  key={`match-${matchStructure.id}`}
                  matchStructure={matchStructure}
                  ctx={bracketCtx}
                />
              ) : (
                <Season2MatchBox
                  key={`match-${matchStructure.id}`}
                  matchStructure={matchStructure}
                  ctx={bracketCtx}
                />
              )
            ))}
          </div>
        </div>
      )}
    </>
  );
};

export default BracketView;